import React, { useState } from 'react';
import { Accordion, Modal, Button } from 'react-bootstrap';
import { useFetchCatalogEntry } from '../hooks/useServices';

export default function TestPlansAccordion({ groupedPlans }) {
  if (!groupedPlans || typeof groupedPlans !== 'object') {
    return <div>No test plans available</div>;
  }

  return (
    <>
      {Object.entries(groupedPlans).map(([arch, squads], archIndex) => (
        <div key={arch} className="mb-4">
          <h5 className="text-success">{arch}</h5>
          <Accordion alwaysOpen>
            {Object.entries(squads).map(([squad, squadTests], idx) => (
              <Accordion.Item eventKey={`${archIndex}-${idx}`} key={squad}>
                <Accordion.Header>
                  {squad} ({squadTests.length} scenarios)
                </Accordion.Header>
                <Accordion.Body>
                  {squadTests.map((plan, index) => (
                    <div key={index} className="plan-item mb-3">
                      <div className="plan-item-header">
                        <strong>{plan.test}</strong>{' '}
                        <span className="plan-jobid">#{plan.job_id}</span>
                      </div>

                      <div className="plan-squad">
                        <strong>Squad:</strong> {plan.squad}
                      </div>

                      {plan.aiAssistedSummary ? (
                        <div className="mt-2">
                          <strong>AI-Assisted Scenario Summary:</strong>
                          <p style={{ whiteSpace: 'pre-wrap' }}>
                            {plan.aiAssistedSummary}
                          </p>
                        </div>
                      ) : (
                        <div className="text-muted mt-2">
                          <strong>AI-Assisted Scenario Summary:</strong>
                          <p>No summary available for this scenario.</p>
                        </div>
                      )}

                      <ModulesList
                        modules={plan.modules || []}
                        matchedCatalogEntries={plan.matchedCatalogEntries || []}
                      />
                    </div>
                  ))}
                </Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </div>
      ))}
    </>
  );
}

function ModulesList({ modules = [], matchedCatalogEntries = [] }) {
  const [expanded, setExpanded] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const {
    entry: fullEntry,
    loading,
    error,
    fetchEntry,
    clearEntry,
  } = useFetchCatalogEntry();

  const THRESHOLD = 10;
  const uniqueModules = [...new Set(modules)].sort();
  const visibleModules = expanded
    ? uniqueModules
    : uniqueModules.slice(0, THRESHOLD);

  const matchedMap = Object.fromEntries(
    matchedCatalogEntries.map((entry) => [entry.testName, entry])
  );

  const handleClick = (modName) => {
    const match = matchedMap[modName];
    if (match) {
      setSelectedEntry(match);
      clearEntry();
      setShowModal(true);
    }
  };

  const loadDetails = () => {
    if (selectedEntry?.testName) {
      fetchEntry(selectedEntry.testName);
    }
  };

  const mergedEntry = {
    ...selectedEntry,
    ...(fullEntry?.testName === selectedEntry?.testName ? fullEntry : {}),
  };

  return (
    <div className="plan-modules mt-2">
      <strong>Used modules:</strong>{' '}
      <span style={{ whiteSpace: 'pre-wrap' }}>
        {visibleModules.map((mod, i) => {
          const isMatched = matchedMap.hasOwnProperty(mod);
          return (
            <React.Fragment key={mod}>
              {isMatched ? (
                <button
                  className="btn btn-sm btn-link p-0 text-decoration-underline text-success"
                  onClick={() => handleClick(mod)}
                >
                  {mod}
                </button>
              ) : (
                <span>{mod}</span>
              )}
              {i < visibleModules.length - 1 && ', '}
            </React.Fragment>
          );
        })}
      </span>
      {uniqueModules.length > THRESHOLD && (
        <div>
          <button
            className="btn btn-sm btn-link mt-1"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? 'Show less' : `Show all (${uniqueModules.length})`}
          </button>
        </div>
      )}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Catalog Entry Info</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {mergedEntry && (
            <>
              <p>
                <strong>Name:</strong> {mergedEntry.testName}
              </p>
              <p>
                <strong>Source:</strong>{' '}
                <a
                  href={mergedEntry.fullPath}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on GitHub
                </a>
              </p>

              {!fullEntry && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={loadDetails}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Show full details'}
                </Button>
              )}

              {fullEntry && (
                <>
                  <p>
                    <strong>AI-Assisted Summary:</strong>
                    <br />
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {mergedEntry.aiAssistedSummary ? (
                        mergedEntry.aiAssistedSummary
                      ) : (
                        <span className="text-muted">
                          No summary available for this test.
                        </span>
                      )}
                    </span>
                  </p>
                  <p>
                    <strong>Description:</strong>
                    <br />
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {mergedEntry.comments || 'No description available.'}
                    </span>
                  </p>
                  <p>
                    <strong>Tags:</strong>{' '}
                    {mergedEntry.tags?.join(', ') || 'None'}
                  </p>
                  <p>
                    <strong>License:</strong> {mergedEntry.license || 'N/A'}
                  </p>
                </>
              )}

              {error && (
                <p className="text-danger mt-2">
                  Failed to load extra info: {error}
                </p>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowModal(false);
              clearEntry();
            }}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
